import { type FormEvent, useState } from 'react';
import { ApiError } from '../api/client';
import { useRemindDocument, useSendDocument } from '../api/hooks';
import type { Document } from '../api/types';
import { Btn, Field, Hint, SwitchRow, TextareaField } from '../components/ui';
import { useUi } from '../state/ui';

/**
 * Odeslání dokladu e-mailem (kap. 43) nebo upomínky: příjemce z kontaktu, volitelná zpráva,
 * ISDOC v příloze. Odchází přes frontu – stav je v sekci E-maily v detailu dokladu.
 */
export function SendSheet({
  doc,
  mode,
  defaultTo,
  onDone,
}: {
  doc: Document;
  mode: 'send' | 'remind';
  defaultTo: string;
  onDone: () => void;
}) {
  const { toast, closeSheet } = useUi();
  const send = useSendDocument(doc.id, doc.companyId);
  const remind = useRemindDocument(doc.id, doc.companyId);
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState('');
  const [message, setMessage] = useState('');
  const [isdoc, setIsdoc] = useState(false);
  const busy = send.isPending || remind.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const done = () => {
      closeSheet();
      toast(
        mode === 'send'
          ? `${doc.number} odchází na ${to || 'e-mail zákazníka'}`
          : `Upomínka k ${doc.number} odchází na ${to || 'e-mail zákazníka'}`,
      );
      onDone();
    };
    const onError = (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Odeslání se nezdařilo');
    if (mode === 'send') {
      send.mutate(
        { to: to || undefined, cc: cc || undefined, message, attachIsdoc: isdoc },
        { onSuccess: done, onError },
      );
    } else {
      remind.mutate({ to: to || undefined, message }, { onSuccess: done, onError });
    }
  };

  return (
    <form onSubmit={submit}>
      <h3>{mode === 'send' ? `Poslat ${doc.number} e-mailem` : `Upomínka k ${doc.number}`}</h3>
      <Field
        id="send-to"
        label="Příjemce (prázdné = e-mail z kontaktu)"
        type="email"
        value={to}
        onChange={(e) => setTo(e.target.value)}
      />
      {mode === 'send' && (
        <Field
          id="send-cc"
          label="Kopie (volitelně, čárkou)"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
        />
      )}
      <TextareaField
        id="send-message"
        label={mode === 'send' ? 'Zpráva navíc (volitelně)' : 'Doplňující text (volitelně)'}
        rows={3}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      {mode === 'send' && (
        <SwitchRow
          title="Přiložit ISDOC"
          subtitle="Pro zákazníky s účetním softwarem"
          checked={isdoc}
          onChange={setIsdoc}
        />
      )}
      <Hint>
        {mode === 'send'
          ? 'PDF se přiloží automaticky; text vychází ze šablony firmy podle jazyka zákazníka.'
          : `${doc.number}: zbývá ${doc.remaining} ${doc.currency}. Upomínka zvýší počítadlo upomínek u dokladu.`}
      </Hint>
      <Btn type="submit" variant="primary" full loading={busy}>
        {mode === 'send' ? 'Odeslat' : 'Poslat upomínku'}
      </Btn>
      <Btn full onClick={closeSheet}>
        Zrušit
      </Btn>
    </form>
  );
}
